import { useEffect, useRef, useState } from "react";

/**
 * Google Identity Services (GIS): el botón oficial de Google.
 *
 * ── Por qué GIS y no el flujo de redirección ────────────────────────
 * El backend también sabe hacer el baile clásico (/auth/google/start →
 * Google → /callback), pero ese baile viaja en una cookie de estado — y en
 * producción el frontend vive en Firebase Hosting hablando con Cloud Run a
 * través de un rewrite que SOLO deja pasar la cookie `__session`. La
 * cookie de estado muere en el camino y el login no cierra nunca. Con GIS
 * no hay baile: el botón entrega un `credential` (un ID token firmado por
 * Google) y lo mandamos por POST del mismo origen a /api/auth/google.
 *
 * ── Qué hace este hook ──────────────────────────────────────────────
 * Carga el script de GIS SOLO cuando una pantalla de acceso lo pide (por
 * eso vive aquí y no en index.html: las demás pantallas no pagan el viaje),
 * pinta el botón oficial dentro de `buttonRef`, y entrega el credential a
 * `onCredential`. Si el script no carga —red caída, bloqueador de
 * anuncios— o falta `VITE_GOOGLE_CLIENT_ID`, el estado queda en
 * `unavailable` y la pantalla enseña un botón deshabilitado que explica
 * por qué: nunca un botón muerto.
 */

const GIS_SRC = "https://accounts.google.com/gsi/client";

/** Si el script no dio señales en este tiempo, se da por no disponible. */
const LOAD_TIMEOUT_MS = 8000;

/**
 * El client id es público por diseño (viaja en cada botón de Google del
 * planeta); lo que NUNCA va al frontend es el client secret.
 *
 * Se lee en una función y no en una constante de módulo para que las
 * pruebas puedan cambiarlo con `vi.stubEnv` sin reimportar el archivo.
 */
function resolveClientId(): string | undefined {
  const raw: unknown = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  return typeof raw === "string" && raw.trim() !== "" ? raw : undefined;
}

// ─── Tipos mínimos de GIS (Google no publica los suyos) ──────────────

interface GisCredentialResponse {
  credential?: string;
}

interface GisButtonOptions {
  type?: "standard" | "icon";
  theme?: "outline" | "filled_blue" | "filled_black";
  size?: "large" | "medium" | "small";
  text?: "signin_with" | "signup_with" | "continue_with" | "signin";
  shape?: "rectangular" | "pill" | "circle" | "square";
  logo_alignment?: "left" | "center";
  width?: number;
  locale?: string;
}

interface GisAccountsId {
  initialize(config: {
    client_id: string;
    callback: (response: GisCredentialResponse) => void;
  }): void;
  renderButton(parent: HTMLElement, options: GisButtonOptions): void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GisAccountsId } };
  }
}

// ─── Carga del script, una sola vez por página ───────────────────────

let scriptPromise: Promise<GisAccountsId> | null = null;

function loadGis(): Promise<GisAccountsId> {
  const already = window.google?.accounts?.id;
  if (already) return Promise.resolve(already);

  scriptPromise ??= new Promise<GisAccountsId>((resolve, reject) => {
    const fail = (reason: string) => {
      // Se limpia la promesa cacheada: un fallo puntual de red no debe
      // condenar todos los intentos futuros de esta pestaña.
      scriptPromise = null;
      reject(new Error(reason));
    };

    const timer = setTimeout(() => fail("GIS no respondió a tiempo"), LOAD_TIMEOUT_MS);

    const script = document.createElement("script");
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      clearTimeout(timer);
      const gis = window.google?.accounts?.id;
      if (gis) resolve(gis);
      else fail("GIS cargó pero no expuso google.accounts.id");
    };
    script.onerror = () => {
      clearTimeout(timer);
      fail("El script de GIS no cargó");
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

// ─── El hook ─────────────────────────────────────────────────────────

export type GoogleIdentityStatus = "loading" | "ready" | "unavailable";

export interface UseGoogleIdentityOptions {
  /** Recibe el credential cuando la persona completa el diálogo de Google. */
  onCredential: (credential: string) => void;
  /** Texto del botón oficial. En el registro, "signup_with". */
  text?: GisButtonOptions["text"];
}

export interface GoogleIdentity {
  status: GoogleIdentityStatus;
  /** El div donde GIS pinta su botón. Va en el JSX de la pantalla. */
  buttonRef: React.RefObject<HTMLDivElement | null>;
}

export function useGoogleIdentity({
  onCredential,
  text = "continue_with",
}: UseGoogleIdentityOptions): GoogleIdentity {
  const [status, setStatus] = useState<GoogleIdentityStatus>(() =>
    resolveClientId() ? "loading" : "unavailable",
  );
  const buttonRef = useRef<HTMLDivElement | null>(null);

  /**
   * El callback vive en un ref para que el efecto de abajo corra UNA vez:
   * si dependiera de `onCredential`, cada render de la pantalla volvería a
   * inicializar GIS y a repintar el botón.
   */
  const credentialRef = useRef(onCredential);
  useEffect(() => {
    credentialRef.current = onCredential;
  });

  useEffect(() => {
    const clientId = resolveClientId();
    if (!clientId) return;

    // Si la pantalla se desmonta antes de que GIS cargue, nadie debe
    // tocar un estado que ya no existe ni pintar en un div muerto.
    let alive = true;

    loadGis()
      .then((gis) => {
        const container = buttonRef.current;
        if (!alive || !container) return;

        gis.initialize({
          client_id: clientId,
          callback: (response) => {
            if (typeof response.credential === "string" && response.credential) {
              credentialRef.current(response.credential);
            }
          },
        });

        // El ancho es fijo en píxeles porque GIS no entiende porcentajes;
        // se mide el contenedor y se acota al máximo que Google permite.
        const width = Math.min(400, Math.max(200, container.offsetWidth || 400));
        container.innerHTML = "";
        gis.renderButton(container, {
          type: "standard",
          theme: "outline",
          size: "large",
          shape: "pill",
          text,
          logo_alignment: "center",
          width,
          locale: "es",
        });

        setStatus("ready");
      })
      .catch(() => {
        if (alive) setStatus("unavailable");
      });

    return () => {
      alive = false;
    };
  }, [text]);

  return { status, buttonRef };
}
